import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, TrendingUp } from "lucide-react";

/**
 * Department TPT (Total Performance Tracker) widget.
 * Admin/HR can pass a `department` prop or leave it empty for company-wide.
 * Department leads always see their own department (backend enforces it).
 */
export default function TptPanel({ department, periodFrom, periodTo, title = "Total Performance Tracker" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = {};
        if (department) params.department = department;
        if (periodFrom) params.period_from = periodFrom;
        if (periodTo) params.period_to = periodTo;
        setLoading(true);
        api.get("/tpt/department", { params })
            .then(({ data }) => setData(data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [department, periodFrom, periodTo]);

    if (loading) {
        return (
            <div className="bg-white border border-ink/10 p-8" data-testid="tpt-panel-loading">
                <div className="overline">Performance</div>
                <h3 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink">{title}</h3>
                <div className="mt-6 text-sm text-ink/40">Loading…</div>
            </div>
        );
    }

    if (!data || (data.rows || []).length === 0) {
        return (
            <div className="bg-white border border-ink/10 p-8" data-testid="tpt-panel-empty">
                <div className="overline">Performance</div>
                <h3 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-klein" /> {title}
                </h3>
                <div className="mt-4 text-sm text-ink/40 py-4 border-t border-ink/5">
                    No completed tasks yet for {data?.department || "this scope"}. Assign and complete daily tasks to start building performance data.
                </div>
            </div>
        );
    }

    const rows = data.rows || [];
    const summary = data.summary || {};
    const top = rows.slice(0, 5);

    return (
        <div className="bg-white border border-ink/10 p-8" data-testid="tpt-panel">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                    <div className="overline">Performance · {data.department}</div>
                    <h3 className="mt-1 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-klein" /> {title}
                    </h3>
                </div>
                <div className="text-right">
                    <div className="overline">Avg TPT</div>
                    <div className="mt-1 font-display font-black text-3xl tabular-nums text-ink">{summary.avg_tpt ?? 0}</div>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-px bg-ink/10 border border-ink/10 mb-5">
                <SmallStat label="Employees" value={summary.total_employees ?? 0} />
                <SmallStat label="Avg completion" value={`${summary.avg_completion ?? 0}%`} />
                <SmallStat label="Top score" value={top[0]?.tpt_score ?? 0} accent />
            </div>
            <ol className="space-y-2">
                {top.map((r, i) => (
                    <li key={r.employee_id} className="flex items-center gap-3 py-2 border-b border-ink/5" data-testid={`tpt-row-${r.employee_id}`}>
                        <div className={`w-7 h-7 flex items-center justify-center font-display font-bold text-sm tabular-nums ${i === 0 ? "bg-klein text-white" : "bg-ink/5 text-ink"}`}>{i + 1}</div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-ink truncate">{r.employee_name}</div>
                            <div className="text-xs text-ink/50 truncate">{r.department || "—"} · {r.totals?.done ?? 0} done / {r.totals?.assigned ?? 0} assigned</div>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="font-display font-bold text-lg tabular-nums text-ink">{r.tpt_score}</div>
                            <div className="text-[10px] uppercase tracking-[0.12em] text-ink/40 flex items-center gap-1 justify-end">
                                <TrendingUp className="w-3 h-3" />{r.completion_rate}%
                            </div>
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
}

function SmallStat({ label, value, accent }) {
    return (
        <div className={`p-4 ${accent ? "bg-klein text-white" : "bg-white"}`}>
            <div className={`overline ${accent ? "!text-white/70" : ""}`}>{label}</div>
            <div className="mt-2 font-display font-black text-xl tabular-nums">{value}</div>
        </div>
    );
}
